/**
 * Building checklist hub — same ChecklistSetEditor as Settings.
 * Sidebar SS list = Settings catalogue (`building_ss_instances`, scoped).
 * Unit types / counts = devices under that SS (`ss_units`).
 */
import { useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import api from '../../utils/api';
import ChecklistSetEditor from '../../components/ChecklistSetEditor';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import { asList } from '../../utils/asList';
import { sortBySsCode, sortSsCodeStrings } from '../../utils/ssCodeSort';
import { filterInstancesForInspectionScope } from '../../utils/inspectionScope';
import { formatSsLabel } from '../../utils/unitDisplay';

export default function BuildingChecklists() {
  const { id: buildingId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const ssCode = searchParams.get('ssCode') || '';
  const unitType = searchParams.get('unitType') || '';

  const { data: building } = useQuery({
    queryKey: ['building', buildingId],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}`);
      return res.data?.data ?? res.data;
    },
  });

  const { data: instances = [] } = useQuery({
    queryKey: ['building', buildingId, 'ss-instances'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${buildingId}/ss-instances`);
      return asList(response.data?.data ?? response.data);
    },
    enabled: !!buildingId,
  });

  const { data: units = [] } = useQuery({
    queryKey: ['building', buildingId, 'ss-units', 'operational'],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}/ss-units`);
      return asList(res.data?.data ?? res.data);
    },
    enabled: !!buildingId,
  });

  const scopedInstances = useMemo(
    () => filterInstancesForInspectionScope(sortBySsCode(asList(instances)), building),
    [instances, building]
  );

  const ssCodes = useMemo(
    () =>
      sortSsCodeStrings(
        scopedInstances.map((i) => String(i.ss_code || '').trim()).filter(Boolean)
      ),
    [scopedInstances]
  );

  const { data: typeRows = [] } = useQuery({
    queryKey: ['checklist-unit-types', buildingId, ssCode],
    queryFn: async () => {
      const res = await api.get('/checklist-sets/unit-types', {
        params: { buildingId, ssCode },
      });
      return res.data?.data ?? [];
    },
    enabled: !!ssCode,
  });

  const { data: ssCards = [] } = useQuery({
    queryKey: ['checklist-ss-cards'],
    queryFn: async () => {
      const res = await api.get('/checklist-sets/ss-cards');
      return res.data?.data ?? [];
    },
  });

  const selectedSsName = useMemo(
    () => ssCards.find((c) => c.ss_code === ssCode)?.name || '',
    [ssCards, ssCode]
  );

  const activeTypeRows = useMemo(
    () => (typeRows || []).filter((t) => Number(t.unit_count) > 0),
    [typeRows]
  );

  useEffect(() => {
    if (!ssCode || !unitType) return;
    if (activeTypeRows.some((t) => t.unit_type === unitType)) return;
    const next = new URLSearchParams(searchParams);
    next.delete('unitType');
    setSearchParams(next, { replace: true });
  }, [ssCode, unitType, activeTypeRows, searchParams, setSearchParams]);

  const selectSs = (code) => {
    const next = new URLSearchParams();
    if (code) next.set('ssCode', code);
    setSearchParams(next);
  };

  const openType = (type) => {
    const next = new URLSearchParams();
    next.set('ssCode', ssCode);
    if (type) next.set('unitType', type);
    setSearchParams(next);
  };

  return (
    <div className="space-y-6">
      <BuildingPageHeading
        buildingId={buildingId}
        buildingName={building?.name}
        title="Checklists"
        infoTitle="Checklists"
        infoChildren={
          <div className="space-y-2 text-sm">
            {building?.name ? <p className="font-medium">{building.name}</p> : null}
            <p>
              SS list matches Edit Building / SS Units / app (inspection scope). Checklist templates are
              shared; the app uses each unit&apos;s confirmed checklist when set.
            </p>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Layers className="w-4 h-4" /> Specified systems
            </h3>
            {ssCodes.length === 0 ? (
              <p className="text-xs text-gray-500">
                No SS in scope. Add SS in <strong>Edit Building</strong>.
              </p>
            ) : (
              <ul className="space-y-1">
                {ssCodes.map((code) => (
                  <li key={code}>
                    <button
                      type="button"
                      onClick={() => selectSs(code)}
                      className={`w-full text-left px-2 py-1.5 rounded text-sm ${
                        ssCode === code
                          ? 'bg-primary-50 text-primary-800 font-semibold'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      {formatSsLabel(code)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {ssCode ? (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Unit types</h3>
              {activeTypeRows.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No unit types yet. Set them under{' '}
                  <Link
                    to={`/buildings/${buildingId}/units?ss=${encodeURIComponent(ssCode)}`}
                    className="text-primary-700 underline"
                  >
                    SS Units
                  </Link>
                  .
                </p>
              ) : (
                <ul className="space-y-1">
                  {activeTypeRows.map((t) => (
                    <li key={t.unit_type}>
                      <button
                        type="button"
                        onClick={() => openType(t.unit_type)}
                        className={`w-full text-left px-2 py-1.5 rounded text-sm ${
                          unitType === t.unit_type
                            ? 'bg-primary-50 text-primary-800 font-semibold'
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {t.unit_type}
                        <span className="ml-1 text-xs text-gray-400">({t.unit_count})</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </aside>

        <div className="lg:col-span-3">
          {ssCode && unitType ? (
            <ChecklistSetEditor
              buildingId={buildingId}
              ssCode={ssCode}
              ssName={selectedSsName}
              scope="UNIT_TYPE"
              unitType={unitType}
              title={`${formatSsLabel(ssCode)}${selectedSsName ? ` — ${selectedSsName}` : ''} / ${unitType}`}
            />
          ) : ssCode ? (
            <div className="space-y-4">
              <ChecklistSetEditor
                buildingId={buildingId}
                ssCode={ssCode}
                ssName={selectedSsName}
                scope="SS_DEFAULT"
                title={`${formatSsLabel(ssCode)}${selectedSsName ? ` — ${selectedSsName}` : ''} (SS default)`}
              />
              <p className="text-xs text-gray-500 text-center">
                Or pick a unit type on the left to edit a type-specific checklist.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-600">
              Select a specified system on the left.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
